import { faDesktop, faFlaskVial, faSignalStream, faStethoscope, IconDefinition } from '@fortawesome/pro-light-svg-icons';
import { useState, useRef } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { Icon } from '@/components/ui/icon.component';
import { MostSearchedResultType, SearchType } from '@/types/most-search-type';
import { MostSearchedType } from '@/app/(main)/(pages)/_types/most-searched.type';

interface ProceduresFilterProps {
  onFilterSelect: (filter: MostSearchedType) => void;
  selectedFilter: MostSearchedType;
  procedures?: MostSearchedResultType[];
}

const CustomPill = ({
  label,
  icon,
  action,
  variant,
  className,
  dataTestId,
}: {
  label: string;
  icon: IconDefinition & IconProp;
  action: () => void;
  variant: 'success' | 'light';
  className?: string;
  dataTestId?: string;
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const handleClick = () => {
    if (!isDragging) {
      action();
    }
    setIsDragging(false);
    startPosRef.current = null;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    startPosRef.current = { x: e.clientX, y: e.clientY };
    setIsDragging(false);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (startPosRef.current) {
      const deltaX = Math.abs(e.clientX - startPosRef.current.x);
      const deltaY = Math.abs(e.clientY - startPosRef.current.y);

      if (deltaX > 10 || deltaY > 10) {
        setIsDragging(true);
      }
    }
  };

  const getVariantClasses = () => {
    switch (variant) {
      case 'success':
        return 'bg-success-100 text-success-700 border-success-400';
      case 'light':
        return 'bg-gray-100 text-gray-700 border-gray-400';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-400';
    }
  };

  return (
    <div
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handleClick}
      className={`flex-shrink-0 px-2 py-1 rounded-md border w-fit flex items-center gap-1 text-base font-semibold cursor-pointer ${getVariantClasses()} ${
        className || ''
      }`}
      data-testid={dataTestId}
    >
      <Icon data-testid="pill-start-icon" icon={icon} size="sm" className="mt-[1px]" />
      <div className="flex justify-content truncate">
        <span data-testid="pill-label" className="truncate">
          {label}
        </span>
      </div>
    </div>
  );
};

export function ProceduresFilter({ onFilterSelect, selectedFilter, procedures }: ProceduresFilterProps) {
  const hasPresentialConsultation = procedures?.some(
    (item) => item.type === SearchType.PRESENTIAL_CONSULTATION && item.procedures.length > 0,
  );
  const hasOnlineConsultation = procedures?.some(
    (item) => item.type === SearchType.ONLINE_CONSULTATION && item.procedures.length > 0,
  );
  const hasImageExam = procedures?.some((item) => item.type === SearchType.IMAGE_EXAM && item.procedures.length > 0);
  const hasLaboratoryExam = procedures?.some(
    (item) => item.type === SearchType.LABORATORIAL_EXAM && item.procedures.length > 0,
  );

  if (Number(procedures?.length) <= 1) {
    return null;
  }

  return (
    <div data-testid={DataTestIdEnum.CITY_MOST_SEARCHED_SECTION_FILTER} className="bg-white xl:bg-transparent">
      <hr className="w-full block xl:hidden border-gray-300 mb-4" />
      <div className="mb-4 w-full">
        <div className="flex overflow-x-auto gap-2 no-scrollbar w-full px-6 sm:px-16 md:px-0 md:mx-auto md:max-w-2xl md:pl-9 xl:p-0 xl:max-w-5xl 3xl:max-w-7xl md:justify-start">
          <div className="flex  gap-2">
            {hasPresentialConsultation && (
              <CustomPill
                dataTestId={DataTestIdEnum.CITY_FILTER_PRESENTIAL_CONSULTATION}
                label="Consultas"
                icon={faStethoscope}
                action={() => {
                  onFilterSelect(SearchType.PRESENTIAL_CONSULTATION);
                }}
                variant={selectedFilter === SearchType.PRESENTIAL_CONSULTATION ? 'success' : 'light'}
                className="cursor-pointer"
              />
            )}
            {hasOnlineConsultation && (
              <CustomPill
                dataTestId={DataTestIdEnum.CITY_FILTER_ONLINE_CONSULTATION}
                label="Teleconsulta"
                icon={faDesktop}
                action={() => {
                  onFilterSelect(SearchType.ONLINE_CONSULTATION);
                }}
                variant={selectedFilter === SearchType.ONLINE_CONSULTATION ? 'success' : 'light'}
                className="cursor-pointer"
              />
            )}
            {hasImageExam && (
              <CustomPill
                dataTestId={DataTestIdEnum.CITY_FILTER_IMAGE_EXAM}
                label="Exames de imagem"
                icon={faSignalStream}
                action={() => {
                  onFilterSelect(SearchType.IMAGE_EXAM);
                }}
                variant={selectedFilter === SearchType.IMAGE_EXAM ? 'success' : 'light'}
                className="cursor-pointer"
              />
            )}
            {hasLaboratoryExam && (
              <CustomPill
                dataTestId={DataTestIdEnum.CITY_FILTER_LABORATORIAL_EXAM}
                label="Exames laboratoriais"
                icon={faFlaskVial}
                action={() => {
                  onFilterSelect(SearchType.LABORATORIAL_EXAM);
                }}
                variant={selectedFilter === SearchType.LABORATORIAL_EXAM ? 'success' : 'light'}
                className="cursor-pointer"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
